/**
 * 번호 지도·재배치 계산 (OcrHub 공용, 순수 함수).
 *
 * 번호 지도(겹침·빈칸 판정)와 번호 다시 매기기(여러 건을 한 번에 옮기기)가 같은 규칙을 써야 한다.
 * 규칙이 갈리면 지도가 "겹쳤다"고 한 것을 재배치가 다르게 세게 된다. 순수 함수라 노드로 검증한다.
 */

// 책 순서의 정렬 키. 정렬값(order_no)이 없는 행은 맨 뒤다 — 서버 목록 정렬(order_no NULLS LAST)과 같다.
// 정답·풀이만 있고 본문이 없는 껍데기 행이 그렇다(매칭이 본문 조각에서만 정렬값을 만든다).
// 종전처럼 0 으로 치면 맨 앞에 서서 1~N 을 한 칸씩 민다 — 운영 프로젝트 23 에서 12번 본문이 빠진 뒤
// 1~11 이 밀린 원인이다(BE-20260914-reply-page-reocr-force.md §4-2). 0 은 「없음」이 아니므로 ?? 로만 가른다.
const orderKey = (p) => p.order_no ?? Number.MAX_SAFE_INTEGER;

// 선택한 문제들을 책 순서(order_no)대로 start부터 연속 부여했을 때의 계획.
// 충돌은 '선택 밖 문제가 이미 쓰는 번호'로만 판정한다 — 선택끼리는 적용 순서로 회피한다(orderForApply).
export function planRenumber(items, selectedIds, start) {
  const selected = selectedIds instanceof Set ? selectedIds : new Set(selectedIds);
  const list = Array.isArray(items) ? items : [];
  const selectedList = list
    .filter((p) => selected.has(p.ocr_prob_id))
    .sort((a, b) => orderKey(a) - orderKey(b));
  const valid = Number.isInteger(start) && start > 0;
  const plan = valid ? selectedList.map((p, i) => ({ p, from: p.problem_number, to: start + i })) : [];
  const outsideNums = new Set();
  list.forEach((p) => {
    if (!selected.has(p.ocr_prob_id) && typeof p.problem_number === 'number') outsideNums.add(p.problem_number);
  });
  const conflicts = plan.filter((r) => outsideNums.has(r.to));
  const changed = plan.filter((r) => r.to !== r.from);
  return { plan, conflicts, changed, selectedList };
}

// 원본 등장 순서(order_no)대로 1부터 다시 매기는 계획.
//
// problem_number를 '지면에 인쇄된 번호'가 아니라 '원본에서 몇 번째로 나오는가'로 본다.
// 그러면 「선택형 1~16 + 서답형 1~5」처럼 번호가 두 벌인 시험지도 1~21로 한 줄이 된다 —
// order_no가 이미 페이지·위치 순이라(프로젝트 8: 75~79 / 1080~1083 / …) 정렬만 하면 그 순서가 나온다.
//
// 두 가지를 강제한다:
//   1. 복제본(is_split)은 맨 뒤로 민다. order_no가 원본과 같아서 섞어 정렬하면 원본과 복제가
//      번갈아 놓여(17,18,19,20…) 순서가 뒤엉킨다. 뒤로 몰면 원본이 1~N, 복제가 그 뒤를 받는다.
//   2. 승격된 문제가 하나라도 있으면 계획을 만들지 않는다. 2026-08-30 계약 변경으로 승격 행의
//      PATCH 는 열렸지만(423 폐기) 이 판단은 그대로 둔다 — 이미 문제은행에 나간 번호를 흔드는
//      일이고, 이 저장소는 재등록을 하지 않아 흔든 번호가 은행에 반영되지도 않는다.
export function planReindexByOrder(items) {
  const list = (Array.isArray(items) ? items : []).filter((p) => typeof p.problem_number === 'number');
  const promoted = list.filter((p) => p.promoted === true);
  if (promoted.length > 0) return { blocked: 'promoted', promoted, plan: [], changed: [], splitCount: 0 };
  const sorted = [...list].sort((a, b) => {
    const sa = a.is_split ? 1 : 0;
    const sb = b.is_split ? 1 : 0;
    if (sa !== sb) return sa - sb;
    const oa = orderKey(a);
    const ob = orderKey(b);
    if (oa !== ob) return oa - ob;
    return (a.ocr_prob_id ?? 0) - (b.ocr_prob_id ?? 0); // 동률에서도 결과가 매번 같도록
  });
  const plan = sorted.map((p, i) => ({ p, from: p.problem_number, to: i + 1 }));
  return {
    blocked: null,
    promoted: [],
    plan,
    changed: plan.filter((r) => r.to !== r.from),
    splitCount: list.filter((p) => p.is_split).length,
  };
}

// 적용 순서 — 목표 번호가 비어 있는 것부터 하나씩 옮긴다.
//
// 단순히 "커지면 큰 것부터"로 정렬하면 연속 재배치(1→17, 2→18 …)는 되지만 맞바꾸기(1→2, 2→1)에서
// 깨진다. 둘이 서로의 번호를 물고 있어 어느 쪽을 먼저 옮겨도 상대의 번호를 밟기 때문이다.
// 그래서 매 단계 "지금 비어 있는 자리"를 찾아 옮기고, 아무도 옮길 수 없으면(= 순환) 한 건을
// 아직 아무도 안 쓰는 임시 번호로 밀어내 고리를 끊은 뒤 계속한다. 그 한 건은 나중에 제 목표로 간다.
//
// 반환: [{ id, to, temp }] — temp는 고리를 끊으려고 잠시 들르는 번호(최종 상태에는 남지 않는다).
export function planApplySteps(items, changed) {
  const cur = new Map();
  (Array.isArray(items) ? items : []).forEach((p) => cur.set(p.ocr_prob_id, p.problem_number));
  const target = new Map(changed.map((r) => [r.p.ocr_prob_id, r.to]));
  const pending = new Set(changed.map((r) => r.p.ocr_prob_id));
  const steps = [];

  const holdersOf = (num, self) => {
    const out = [];
    cur.forEach((n, id) => { if (n === num && id !== self) out.push(id); });
    return out;
  };
  const maxUsed = () => {
    let m = 0;
    cur.forEach((n) => { if (typeof n === 'number' && n > m) m = n; });
    target.forEach((n) => { if (n > m) m = n; });
    return m;
  };

  let guard = 0;
  const limit = changed.length * 4 + 8; // 순환을 끊는 임시 이동을 넉넉히 잡아도 이 안에서 끝난다
  while (pending.size > 0 && guard < limit) {
    guard += 1;
    let moved = false;
    for (const id of [...pending]) {
      const to = target.get(id);
      if (holdersOf(to, id).length === 0) {
        steps.push({ id, to, temp: false });
        cur.set(id, to);
        pending.delete(id);
        moved = true;
        break; // 한 건 옮길 때마다 점유 상황이 바뀌므로 다시 처음부터 본다
      }
    }
    if (!moved) {
      const id = [...pending][0];
      const tmp = maxUsed() + 1;
      steps.push({ id, to: tmp, temp: true });
      cur.set(id, tmp); // pending에 남겨 둔다 — 다음 바퀴에 제 목표로 옮겨진다
    }
  }
  return steps;
}

// scope별 번호 지도 — 겹친 번호와 빈 번호.
// 격자 길이는 '최대 번호'가 아니라 '문제 수'까지 늘린다. 번호가 겹쳐 있으면 최대 번호가
// 실제 필요한 번호 수보다 작아, 옮겨 갈 빈 자리가 지도에서 통째로 사라지기 때문이다.
export function buildScopeMaps(items) {
  const byScope = new Map();
  (Array.isArray(items) ? items : []).forEach((p) => {
    const key = p.scope || '__single__';
    if (!byScope.has(key)) byScope.set(key, []);
    byScope.get(key).push(p);
  });
  return [...byScope.entries()].map(([scope, list]) => {
    const byNum = new Map();
    list.forEach((p) => {
      const n = p.problem_number;
      if (typeof n !== 'number') return;
      if (!byNum.has(n)) byNum.set(n, []);
      byNum.get(n).push(p);
    });
    const nums = [...byNum.keys()].sort((a, b) => a - b);
    const max = nums.length ? nums[nums.length - 1] : 0;
    const span = Math.max(max, list.length);
    const dupes = nums.filter((n) => byNum.get(n).length > 1);
    const gaps = [];
    for (let n = 1; n <= span; n += 1) if (!byNum.has(n)) gaps.push(n);
    return { scope, count: list.length, byNum, max, span, dupes, gaps };
  });
}
